import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, IconButton,
    TableHead, TableRow, TextField, Button, CircularProgress, Chip, Stack, Grid, Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Fastfood as FastfoodIcon, Info as InfoIcon } from '@mui/icons-material';
import ConfirmationDialog from '../../../ReusableDailog';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CrudButtons from '../../../../GlobalFunction/CrudButtons';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import BasicPrint from './BasicPrint';

const BasicRatio = () => {
    const [bomData, setBomData] = useState([]);
    const [firstColumn, setFirstColumn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userId] = useState(sessionStorage.getItem('UserId'));
    const [saving, setSaving] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const globalProperty = useSelector(selectGlobalProperty);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const foodBomIdFromUrl = queryParams.get('foodBomId');
    const [foodBomId, setFoodBomId] = useState(foodBomIdFromUrl ? Number(foodBomIdFromUrl) : null);
    const contentRef = useRef(null);
    const handlePrint = useReactToPrint({ contentRef });

    useEffect(() => {
        fetchBomData();
    }, [globalProperty]);

    const fetchBomData = async () => {
        if (!foodBomId) {
            toast.error("No BOM ID provided");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await axiosInstance.post('MstFood/RetriveMstFoodBomItm', {
                FoodBomId: foodBomId,
                Flag: "",
                PropId: globalProperty,
            });

            if (response.data.Status === 0 && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                const firstItem = response.data.Data[0];

                let firstNmKey = null;
                let firstNum = null;

                Object.keys(firstItem).some(key => {
                    if (key.startsWith('EQNm') && key.match(/^EQNm\d+$/)) {
                        const num = parseInt(key.replace('EQNm', ''), 10);
                        const value = String(firstItem[key]).trim();
                        if (value !== '') {
                            firstNmKey = key;
                            firstNum = num;
                            return true;
                        }
                    }
                    return false;
                });

                if (firstNmKey && firstNum !== null) {
                    const col = {
                        key: `EQ${firstNum}`,
                        label: firstItem[firstNmKey],
                    };
                    setFirstColumn(col);

                    const normalized = response.data.Data.map(item => {
                        const row = {
                            ItmName: item.ItmName || '—',
                            ...item,
                        };
                        row[col.key] = row[col.key] != null && !isNaN(Number(row[col.key]))
                            ? Number(row[col.key])
                            : 0;
                        return row;
                    });

                    setBomData(normalized);
                } else {
                    setBomData([]);
                    setFirstColumn(null);
                }
            } else {
                toast.warn(response.data.Message);
                setBomData([]);
                setFirstColumn(null);
            }
        } catch (error) {
            toast.error("Failed to load BOM items");
            setBomData([]);
            setFirstColumn(null);
        } finally {
            setLoading(false);
        }
    };

    const foodName = bomData.length > 0 ? bomData[0]?.FoodName || '—' : 'Not loaded';

    const handleQtyChange = (rowIndex, value) => {
        if (!firstColumn) return;
        setBomData(prev => {
            const next = [...prev];
            next[rowIndex] = {
                ...next[rowIndex],
                [firstColumn.key]: value === '' ? 0 : Number(value),
            };
            return next;
        });
    };

    const handleSave = async () => {
        if (bomData.length === 0 || !firstColumn) {
            toast.info("Nothing to save");
            return;
        }

        setSaving(true);

        try {
            const payload = bomData.map(item => {
                const base = {
                    FoodBomItmId: item.FoodBomItmId || 0,
                    FoodBomDtlId: item.FoodBomDtlId || 0,
                    FoodBomId: item.FoodBomId || foodBomId,
                    PropId: globalProperty,
                    ItmId: item.ItmId || 0,
                    StdQty: item.StdQty ?? 200,
                    Remark: item.Remark || "",
                    CreatedBy: userId || 1,
                    UpdatedBy: userId,
                };

                base[firstColumn.key] = Number(item[firstColumn.key]) || 0;

                const nmKey = `EQNm${firstColumn.key.replace('EQ', '')}`;
                base[nmKey] = item[nmKey] || firstColumn.label;

                return base;
            });

            const response = await axiosInstance.post('MstFood/UpsertMstFoodBomItm', payload);

            if (response.data.Status === 0) {
                toast.success("Basic ratio updated successfully");
                setTimeout(() => {
                    fetchBomData();
                }, 2000);
            }
        } catch (error) {
            toast.error(error.response?.data?.Message || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const handleCloseConfirmDialog = () => setOpenConfirmDialog(false);

    const handleConfirmDelete = async () => {
        setOpenConfirmDialog(false);
        try {
            const response = await axiosInstance.post("MstFood/DeleteMstFoodBomItm", {
                FoodBomId: foodBomId,
            });
            if (response.data.Status === 0) {
                toast.success(response.data.Message || 'Record deleted');
            } else {
                toast.warn(response.data.Message || 'Delete failed');
            }
        } catch (error) {
            toast.error("Delete operation failed");
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size="2.8rem" />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
            <ToastContainer />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
                flexWrap="wrap"
                gap={1.5}
            >
                <Typography variant="h6" fontWeight={700} sx={{color: '#635bff'}}>
                    Basic Ratio Quantity
                </Typography>

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                        disabled={saving}
                        onClick={handleSave}
                        sx={{ borderColor: '#16a34a', color: '#16a34a', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#15803d', background: 'rgba(22,163,74,0.1)', } }}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => setOpenConfirmDialog(true)}
                        sx={{ borderColor: '#ef4444', color: '#ef4444', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#b91c1c', background: 'rgba(239,68,68,0.1)' } }}
                    >
                        Delete
                    </Button>

                    <Tooltip title='Check Table' arrow>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<TableRowsIcon fontSize="small" />}
                                onClick={() => navigate('/stdbomtable')}
                                sx={{ borderColor: '#7c3aed', color: '#7c3aed', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#6d28d9', background: 'rgba(124,58,237,0.1)' } }}
                            >
                                Table
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title='Move to Update Quantity' arrow>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowForwardIcon fontSize="small" />}
                                onClick={() => navigate(`/updatebomqty?foodBomId=${foodBomId}`)}
                                sx={{ borderColor: '#2563eb', color: '#2563eb', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#1d4ed8', background: 'rgba(37,99,235,0.1)' } }}
                            >
                                Update
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title='Close Window' arrow>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloseIcon fontSize="small" />}
                                onClick={() => window.close()}
                                sx={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#0891b2', background: 'rgba(13,148,136,0.1)' } }}
                            >
                                Close
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Compact Info */}
            <Paper
                elevation={2}
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <InfoIcon fontSize="small" color="warning" />
                        <Typography variant="body2" fontWeight={600}>
                            BOM ID:
                        </Typography>
                        <Chip label={foodBomId} size="small" color="secondary" />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" >
                        <FastfoodIcon fontSize="small" color="success" />
                        <Typography variant="body2" fontWeight={600}>
                            Food:
                        </Typography>
                        <Typography variant="body1" color='primary' fontWeight={600}>
                            {foodName}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" >
                        <AccessibilityIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={600}>
                            Qty:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {bomData[0]?.EQNm0}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" >
                        <ArrowUpwardIcon fontSize="small" color="secondary" />
                        <Typography variant="body2" fontWeight={600}>
                            Inc Qty:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {bomData[0]?.StdMealIncre}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" >
                        <IconButton onClick={handlePrint}>
                            <PrintIcon fontSize="small" color="primary" />
                        </IconButton>
                    </Stack>
                </Stack>
            </Paper>

            <Paper
                elevation={2}
                sx={{
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    maxWidth: '100%',
                }}
            >
                <TableContainer sx={{ maxHeight: 620, overflowX: 'auto' }}>
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            '& .MuiTableCell-root': {
                                py: 0.5,
                                px: 0.9,
                                fontSize: '0.84rem',
                            },
                            '& .MuiTableCell-head': {
                                py: 0.9,
                                px: 1,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                bgcolor: 'grey.50',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell
                                    sx={{
                                        minWidth: 150,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 15,
                                        bgcolor: 'grey.100',
                                        fontWeight: 700,
                                        boxShadow: '2px 0 6px -3px rgba(0,0,0,0.12)',
                                        borderRight: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                >
                                    Ingredient
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        minWidth: 20,
                                        fontWeight: 700,
                                        bgcolor: 'grey.100',
                                        borderLeft: '1px solid',
                                        borderRight: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    Units
                                </TableCell>

                                {firstColumn && (
                                    <TableCell
                                        align="center"
                                        sx={{
                                            minWidth: 80,
                                            width: 80,
                                            p: 0.5,
                                            borderLeft: '1px solid',
                                            borderColor: 'grey.300',
                                        }}
                                    >
                                        <Chip
                                            label={firstColumn.label}
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.73rem',
                                                fontWeight: 700,
                                                minWidth: 60,
                                                bgcolor: '#635bff',
                                                color: 'white',
                                                borderRadius: '10px',
                                            }}
                                        />
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {bomData.map((row, idx) => (
                                <TableRow
                                    key={idx}
                                    hover
                                    sx={{
                                        '&:nth-of-type(odd)': { bgcolor: 'grey.25' },
                                        '&:hover': { bgcolor: 'primary.50' },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            position: 'sticky',
                                            left: 0,
                                            bgcolor: 'background.paper',
                                            zIndex: 10,
                                            fontWeight: 500,
                                            boxShadow: '2px 0 5px -3px rgba(0,0,0,0.08)',
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            whiteSpace: 'nowrap',
                                            py: 0.5,
                                            px: 0.9,
                                        }}
                                    >
                                        {row.ItmName}
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontSize: '0.84rem',
                                            color: 'text.primary',
                                            borderLeft: '1px solid',
                                            borderRight: '1px solid',
                                            borderColor: 'grey.200',
                                        }}
                                    >
                                        <Chip size='small'
                                            label={row.UnitName || '—'}
                                            sx={{
                                                height: 22,
                                                fontSize: '0.8rem',
                                                bgcolor: 'green',
                                                color: 'white',
                                                borderRadius: '10px',
                                            }}
                                        />
                                    </TableCell>

                                    {firstColumn && (
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={row[firstColumn.key]}
                                                onChange={e => handleQtyChange(idx, e.target.value)}
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.1,
                                                    style: { textAlign: 'center', fontSize: '0.83rem', padding: '3px 5px', height: 22 },
                                                }}
                                                sx={{
                                                    width: 68,
                                                    '& .MuiOutlinedInput-root': {
                                                        height: 28,
                                                        borderRadius: 1,
                                                        bgcolor: row[firstColumn.key] > 0 ? 'success.50' : 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}

                            {bomData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                                        No ingredients found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box
                    sx={{
                        p: 1,
                        bgcolor: 'grey.50',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.78rem',
                        color: 'text.secondary',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <span>{bomData.length} ingredient{bomData.length !== 1 ? 's' : ''}</span>
                    <span>Basic Ratio </span>
                </Box>
            </Paper>

            <Box sx={{ display: 'none' }}>
                <div ref={contentRef}>
                    <BasicPrint
                        bomData={bomData}
                        foodName={foodName}
                        foodBomId={foodBomId}
                    />
                </div>
            </Box>

            <ConfirmationDialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleConfirmDelete}
                title="Confirm Deletion"
                message="Are you sure you want to delete this Basic Ratio record?"
            />
        </Box>
    );
};

export default BasicRatio;